import AdminUserAffectationsCard from '@/components/Affectations/AdminUserAffectationsCard';
import {
  useGetAffectationCountsByUserQuery,
  useGetAffectationsQuery,
} from '@/features/api/endpoints/affectationEndpoints';
import UserSearch from '@/components/common/UserSearch';
import type { Affectation, DirectusUsers } from '@/models/types';
import { Badge, Center, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { useDocumentTitle } from '@mantine/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { useTranslate } from '@tolgee/react';
import isObject from 'lodash/isObject';
import { useMemo, useState } from 'react';

type UserGroup = {
  userId: string;
  user: Affectation['user'];
  affectations: Affectation[];
};

/**
 * Regroupe les affectations par utilisateur. Les affectations arrivant triées par date
 * décroissante, l'ordre d'insertion dans la Map place en premier l'utilisateur ayant
 * la demande la plus récente.
 */
const groupByUser = (affectations: Affectation[]): UserGroup[] => {
  const byUser = new Map<string, UserGroup>();
  affectations.forEach(affectation => {
    const userId = isObject(affectation.user) ? affectation.user.id : (affectation.user ?? '');
    const group = byUser.get(userId);
    if (group) {
      group.affectations.push(affectation);
    } else {
      byUser.set(userId, { userId, user: affectation.user, affectations: [affectation] });
    }
  });
  return [...byUser.values()];
};

const AdminAffectationsPage = () => {
  const { t } = useTranslate();
  const [selectedUser, setSelectedUser] = useState<DirectusUsers | null>(null);

  useDocumentTitle(t('admin.affectations.title'));

  const {
    data: affectations = [],
    isLoading,
    isError,
  } = useGetAffectationsQuery({
    fields: [
      '*',
      {
        user: ['id', 'first_name', 'last_name', 'email'],
        role: ['name'],
        etablissement: ['name'],
      },
    ],
    filter: {
      status: {
        _eq: 'draft',
      },
    },
    sort: ['-date_created'],
    limit: -1,
  });

  const groups = useMemo(() => groupByUser(affectations), [affectations]);

  // L'utilisateur recherché peut ne pas avoir de `draft` : sa carte s'affiche alors sans
  // demande en attente, pour consulter ses autres affectations.
  const visibleGroups = useMemo((): UserGroup[] => {
    if (!selectedUser) {
      return groups;
    }
    const group = groups.find(({ userId }) => userId === selectedUser.id);
    return [group ?? { userId: selectedUser.id, user: selectedUser, affectations: [] }];
  }, [groups, selectedUser]);

  // Compteurs des onglets « Active » et « Refusée » : une seule requête groupée
  // pour tous les utilisateurs affichés.
  const { data: countsByUser = {} } = useGetAffectationCountsByUserQuery(
    visibleGroups.length > 0
      ? {
          filter: {
            user: { _in: visibleGroups.map(({ userId }) => userId) },
            status: { _in: ['published', 'archived'] },
          },
        }
      : skipToken,
  );

  return (
    <Stack gap="md">
      {/* Le Group passe à la ligne : sur petit écran, la recherche (pleine largeur)
          se place sous le titre. */}
      <Group justify="space-between" align="center" gap="sm">
        <Group gap="sm" align="center">
          <Title order={1}>{t('admin.affectations.title')}</Title>
          {!isLoading && !isError && (
            <Badge size="lg" color={affectations.length > 0 ? 'yellow' : 'gray'} variant="light">
              {t('admin.affectations.total', { count: affectations.length })}
            </Badge>
          )}
        </Group>

        <UserSearch
          w={{ base: '100%', sm: 360 }}
          aria-label={t('admin.affectations.filter.user')}
          placeholder={t('admin.affectations.filter.user.placeholder')}
          onUserSelect={setSelectedUser}
        />
      </Group>

      {isLoading ? (
        <Center h={200}>
          <Loader size="sm" />
        </Center>
      ) : isError ? (
        <Text c="red" role="alert">
          {t('admin.affectations.error')}
        </Text>
      ) : visibleGroups.length === 0 ? (
        <Text c="dimmed">{t('admin.affectations.empty')}</Text>
      ) : (
        visibleGroups.map(({ userId, user, affectations: userAffectations }) => (
          <AdminUserAffectationsCard
            key={userId}
            userId={userId}
            user={user}
            affectations={userAffectations}
            counts={countsByUser[userId]}
          />
        ))
      )}
    </Stack>
  );
};

export default AdminAffectationsPage;
